/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { ExpiryAutomation, ExpiryDoc, ExpiryService, ExpiryStatus } from './expiry.service';
import { SetExpiryDialogComponent } from './set-expiry-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-document-expiry',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, PageHeaderComponent, PageStateComponent, ThaiDatePipe, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './document-expiry.component.html',
  styleUrl: './document-expiry.component.scss'
})
export class DocumentExpiryComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly expiry = inject(ExpiryService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /**
   * Whether the screen is shown inside the settings page rather than as a page
   * of its own. It then drops its own page header, because the page it sits in
   * already carries one.
   */
  @Input() embedded = false;

  docs: ExpiryDoc[] = [];
  loading = true;
  search = '';

  /**
   * The screen opens on what its name promises.
   *
   * The list behind it is every document that carries an expiry date, which is
   * what the page is for managing — but showing all of them by default put
   * documents due in a year beside ones that expired months ago, and the page
   * read as if it could not tell them apart. It opens on the expired ones; the
   * other states are one tab away, each with its own count.
   */
  statusFilter: 'all' | ExpiryStatus = 'expired';
  busyId = '';

  readonly filters: Array<{ key: 'all' | ExpiryStatus; label: string }> = [
    { key: 'expired', label: 'DMS.UI.1BDA9A' },
    { key: 'soon', label: 'DMS.UI.F4B86D' },
    { key: 'active', label: 'DMS.UI.3DE671' },
    { key: 'all', label: 'DMS.UI.BD5D57' }
  ];

  ngOnInit(): void {
    // the settings page links straight to one status, so arriving from there
    // lands on the tab it named rather than on the default one
    const status = this.route.snapshot.queryParamMap.get('status');
    if (status === 'expired' || status === 'soon' || status === 'active' || status === 'all') {
      this.statusFilter = status;
    }
    this.load();
    this.loadAutomation();
  }

  /** The nightly job that archives expired documents, and how it is doing. */
  private loadAutomation(): void {
    this.expiry
      .automation()
      .pipe(catchError(() => of(null)))
      .subscribe((state) => this.automation.set(state));
  }

  /** Turns the nightly job on or off, creating it the first time. */
  setAutomation(enabled: boolean): void {
    const current = this.automation();
    if (!current || this.autoBusy()) {
      return;
    }
    this.autoBusy.set(true);
    this.expiry.setAutomation(current, enabled, this.translate.instant('DMS.UI.RET_AUTO_JOB_NAME')).subscribe({
      next: () => {
        this.autoBusy.set(false);
        this.notification.showInfo(this.translate.instant(enabled ? 'DMS.UI.RET_AUTO_ENABLED' : 'DMS.UI.RET_AUTO_DISABLED'));
        this.loadAutomation();
      },
      error: () => {
        this.autoBusy.set(false);
        this.notification.showError(this.translate.instant('DMS.UI.RET_AUTO_SAVE_FAILED'));
      }
    });
  }

  /** Runs the job now; the list and the job's state are read again once it has had a moment. */
  runAutomation(): void {
    const job = this.automation()?.job;
    if (!job?.id || this.autoBusy()) {
      return;
    }
    this.autoBusy.set(true);
    this.expiry.runAutomation(job.id).subscribe({
      next: () => {
        this.notification.showInfo(this.translate.instant('DMS.UI.RET_AUTO_STARTED'));
        setTimeout(() => {
          this.autoBusy.set(false);
          this.load();
          this.loadAutomation();
        }, 5000);
      },
      error: () => {
        this.autoBusy.set(false);
        this.notification.showError(this.translate.instant('DMS.UI.RET_AUTO_RUN_FAILED'));
      }
    });
  }

  private load(): void {
    this.loading = true;
    this.expiry.listWithExpiry().subscribe({
      next: (docs) => {
        this.docs = docs;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notification.showError(this.translate.instant('DMS.UI.0D1647'));
      }
    });
  }

  reload(): void {
    this.load();
  }

  /** The nightly expiry job; null until read, or when it could not be. */
  readonly automation = signal<ExpiryAutomation | null>(null);
  readonly autoBusy = signal(false);

  /** Days until expiry; negative once the date has passed. */
  daysLeft(doc: ExpiryDoc): number {
    return Math.ceil((new Date(doc.expiry).getTime() - Date.now()) / 86400000);
  }

  daysLabel(doc: ExpiryDoc): string {
    const days = this.daysLeft(doc);
    if (days < 0) {
      return this.translate.instant('DMS.UI.9C179E', { p0: Math.abs(days) });
    }
    return days === 0 ? this.translate.instant('DMS.UI.0491EA') : this.translate.instant('DMS.UI.1BFE71', { p0: days });
  }

  /** Total size of the documents in one status, or null when none carries one. */
  sizeOf(key: 'all' | ExpiryStatus): string {
    const docs = key === 'all' ? this.docs : this.docs.filter((d) => d.status === key);
    const bytes = docs.reduce((sum, d) => sum + (d.sizeInBytes ?? 0), 0);
    if (!bytes) {
      return '';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
      value /= 1024;
      i++;
    }
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  /** Thai label for a dms:documentType value, falling back to the raw value. */
  typeLabel(value: string): string {
    return DmsMetadataService.label(value);
  }

  initial(name?: string): string {
    return (name || '?').trim().charAt(0).toUpperCase();
  }

  private extension(name: string): string {
    const parts = (name ?? '').toLowerCase().split('.');
    return parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  }

  badge(doc: ExpiryDoc): string {
    return this.extension(doc.name).toUpperCase().slice(0, 4) || 'DOC';
  }

  badgeClass(doc: ExpiryDoc): string {
    return `aca-exp__badge--${this.extension(doc.name) || 'file'}`;
  }

  trackById(_index: number, doc: ExpiryDoc): string {
    return doc.id;
  }

  countOf(key: 'all' | ExpiryStatus): number {
    return key === 'all' ? this.docs.length : this.docs.filter((d) => d.status === key).length;
  }

  filtered(): ExpiryDoc[] {
    const q = this.search.trim().toLowerCase();
    return this.docs.filter((d) => {
      if (this.statusFilter !== 'all' && d.status !== this.statusFilter) {
        return false;
      }
      if (q && !`${d.name} ${d.path}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }

  statusLabel(s: ExpiryStatus): string {
    return s === 'expired' ? 'DMS.UI.1BDA9A' : s === 'soon' ? 'DMS.UI.F4B86D' : 'DMS.UI.3DE671';
  }

  statusIcon(s: ExpiryStatus): string {
    return s === 'expired' ? 'error' : s === 'soon' ? 'warning' : 'check_circle';
  }

  open(d: ExpiryDoc): void {
    this.router.navigate(['/document', d.id]);
  }

  edit(d: ExpiryDoc): void {
    this.dialog
      .open(SetExpiryDialogComponent, { width: '420px', maxWidth: '94vw', autoFocus: false, data: { nodeId: d.id, name: d.name, current: d.expiry } })
      .afterClosed()
      .subscribe((ok) => {
        if (ok) {
          this.load();
        }
      });
  }

  /* ---- bulk selection --------------------------------------------------- */

  /** Ids of the checked rows — kept across reloads so a refresh keeps the set. */
  readonly selected = signal<Set<string>>(new Set());

  isSel(d: ExpiryDoc): boolean {
    return this.selected().has(d.id);
  }

  selCount(): number {
    return this.selected().size;
  }

  allSel(): boolean {
    const rows = this.filtered();
    return rows.length > 0 && rows.every((d) => this.selected().has(d.id));
  }

  /** Header checkbox shows the dash state when only part of the list is picked. */
  someSel(): boolean {
    const rows = this.filtered();
    return !this.allSel() && rows.some((d) => this.selected().has(d.id));
  }

  toggleSel(d: ExpiryDoc): void {
    const next = new Set(this.selected());
    if (next.has(d.id)) {
      next.delete(d.id);
    } else {
      next.add(d.id);
    }
    this.selected.set(next);
  }

  toggleAll(): void {
    if (this.allSel()) {
      this.selected.set(new Set());
      return;
    }
    this.selected.set(new Set(this.filtered().map((d) => d.id)));
  }

  clearSel(): void {
    this.selected.set(new Set());
  }

  /** One dialog sets the same expiry on every checked document. */
  bulkEdit(): void {
    const ids = [...this.selected()];
    if (!ids.length) {
      return;
    }
    this.dialog
      .open(SetExpiryDialogComponent, {
        width: '420px',
        maxWidth: '94vw',
        autoFocus: false,
        data: { nodeIds: ids, name: `${ids.length} ${this.translate.instant('DMS.UI.4514CB')}` }
      })
      .afterClosed()
      .subscribe((ok) => {
        if (ok) {
          this.clearSel();
          this.load();
        }
      });
  }

  /** Trash every checked document; rows that fail stay so they can be retried. */
  bulkTrash(): void {
    const docs = this.filtered().filter((d) => this.selected().has(d.id));
    if (!docs.length || !confirm(this.translate.instant('DMS.UI.RET_BULK_CONFIRM', { p0: docs.length }))) {
      return;
    }
    this.busyId = '*';
    forkJoin(docs.map((d) => this.expiry.trash(d.id).pipe(catchError(() => of(null))))).subscribe((results) => {
      const done = new Set(docs.filter((_d, i) => results[i] !== null).map((d) => d.id));
      this.busyId = '';
      this.docs = this.docs.filter((x) => !done.has(x.id));
      this.selected.set(new Set([...this.selected()].filter((id) => !done.has(id))));
      if (done.size === docs.length) {
        this.notification.showInfo(this.translate.instant('DMS.UI.RET_BULK_DONE', { p0: done.size }));
      } else {
        this.notification.showError(this.translate.instant('DMS.UI.RET_BULK_PARTIAL', { p0: done.size, p1: docs.length }));
      }
    });
  }

  trash(d: ExpiryDoc): void {
    if (!confirm(this.translate.instant('DMS.UI.C3917C', { p0: d.name }))) {
      return;
    }
    this.busyId = d.id;
    this.expiry.trash(d.id).subscribe({
      next: () => {
        this.busyId = '';
        this.notification.showInfo(this.translate.instant('DMS.UI.1A0231'));
        this.docs = this.docs.filter((x) => x.id !== d.id);
      },
      error: (e) => {
        this.busyId = '';
        this.notification.showError(e?.response?.body?.error?.briefSummary || 'DMS.UI.B3A943');
      }
    });
  }
}
